import { Container, Particle, ParticleContainer, Texture } from 'pixi.js';

import { AssetManager } from '../AssetManager';
import { TerrainParticleConfig } from '../data/FootstepParticleConfig';
import { SpritesheetService } from './SpritesheetService';

interface ActiveParticle {
  particle: Particle;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}
const GRAVITY = 200;
const DRAG = 0.95;
const VX_SPREAD = 40; // Horizontal spread range
const VY_BASE = -30; // Initial upward boost target
const VY_VAR = 30; // Upward variance
const LIFE_BASE = 0.3;
const LIFE_VAR = 0.2;

export class FootstepParticleService {
  private container: ParticleContainer;
  private activeParticles: ActiveParticle[] = [];
  private spritesheetService: SpritesheetService;
  private particleTexture: Texture | null = null;
  private hasWarnedTextureMissing = false;

  constructor(spritesheetService: SpritesheetService) {
    this.spritesheetService = spritesheetService;

    // Create container with dynamic properties we intend to change
    this.container = new ParticleContainer({
      // @ts-expect-error: maxSize is valid in runtime but missing in types
      maxSize: 2000,
      dynamicProperties: {
        position: true,
        alpha: true,
        rotation: true,
        scale: true,
        color: true,
      },
    });

    // Load the particle texture on init
    this.initialise();
  }

  private async initialise() {
    try {
      // Load the particle spritesheet metadata
      await this.spritesheetService.loadSpritesheet('particle');

      // Retrieve the generated texture from AssetManager
      this.particleTexture = AssetManager.getInstance().getTexture('particle_1');
    } catch (e) {
      console.warn('[FootstepParticleService] Failed to load particle spritesheet', e);
    }
  }

  public setStage(stage: Container) {
    stage.addChild(this.container);
    // Render particles at ground level (zIndex 0)
    this.container.zIndex = 0; // Simple ground level
  }

  public emit(worldX: number, worldY: number, config: TerrainParticleConfig) {
    if (!this.particleTexture) {
      // Lazy load texture if not available on init
      if (this.spritesheetService.isLoaded('particle')) {
        this.particleTexture = AssetManager.getInstance().getTexture('particle_1');
      } else {
        if (!this.hasWarnedTextureMissing) {
          console.warn('[FootstepParticleService] Particle texture not loaded yet');
          this.hasWarnedTextureMissing = true;
        }
        return;
      }
    }

    const baseQuantity = Math.round(config.quantity);
    const count = Math.floor(baseQuantity / 2) + Math.floor(Math.random() * baseQuantity);

    for (let i = 0; i < count; i++) {
      const particle = new Particle({
        texture: this.particleTexture!,
        x: worldX,
        y: worldY,
        alpha: 1.0,
        tint: config.colours[Math.floor(Math.random() * config.colours.length)],
        scaleX: 1,
        scaleY: 1,
      });

      this.container.addParticle(particle);

      // Random small velocity
      // Up (-y) and spread x
      const vx = (Math.random() - 0.5) * VX_SPREAD;
      const vy = VY_BASE - Math.random() * VY_VAR;

      const life = LIFE_BASE + Math.random() * LIFE_VAR;

      this.activeParticles.push({
        particle,
        vx,
        vy,
        life,
        maxLife: life,
      });
    }
  }

  public tick(deltaTime: number) {
    if (this.activeParticles.length === 0) return;

    for (let i = this.activeParticles.length - 1; i >= 0; i--) {
      const p = this.activeParticles[i];
      p.life -= deltaTime;

      if (p.life <= 0) {
        this.container.removeParticle(p.particle);
        this.activeParticles.splice(i, 1);
        continue;
      }

      // Physics
      p.vy += GRAVITY * deltaTime;
      p.vx *= DRAG; // Air resistance

      p.particle.x += p.vx * deltaTime;
      p.particle.y += p.vy * deltaTime;

      // Fade out
      p.particle.alpha = p.life / p.maxLife;
    }
  }

  public reset() {
    // Remove all particles
    // Helper to clear container
    while (this.activeParticles.length > 0) {
      const p = this.activeParticles.pop();
      if (p) {
        this.container.removeParticle(p.particle);
      }
    }
  }
}
